import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { 
  Box, Input, InputGroup, InputRightElement, Button, Text, Link, Spinner, 
  HStack, Divider, Checkbox, Modal, ModalOverlay, ModalContent, ModalHeader, 
  ModalBody, ModalCloseButton, useToast,
} from "@chakra-ui/react";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import EyeOpen from "assets/svg/eye-open.svg";
import EyeClose from "assets/svg/eye-close.svg";
import AppleLogo from "assets/svg/apple.svg";
import GoogleLogo from "assets/svg/google.svg";
import { useAuth } from "auth/authProvider";
import EmailVerification from "./EmailVerification";
import SocialSignupModal from "./SocialSignUpModal";
import { signInWithPopup, GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import { auth } from "auth/firebase/firebase_config";
import { getPrivacyPolicyOrTerms } from "auth/gridAuthClient";

const FIELD_W = "260px";
const inputSt = {
  bg: "transparent", 
  border: "1px solid #fff", 
  borderRadius: "10px",
  color: "#fff", 
  fontSize: "13px", 
  p: "10px", 
  w: "100%"
};

const SocialButton = React.memo(({ onClick, logo, label, disabled }) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    display="flex" 
    alignItems="center" 
    gap="8px" 
    w="125px" 
    h="40px"
    bg="transparent" 
    color="#fff" 
    border="1px solid #fff" 
    borderRadius="10px"
    fontSize="13px" 
    _hover={{ bg: "#fff", color: "#000" }}
  >
    <img src={logo} alt={label} width={label === "Google" ? 16 : 18} />
    {label}
  </Button>
));

export default function SignUpSteps({ onBack }) {
  const { signup, socialSignup } = useAuth();
  const isMountedRef = useRef(true);
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    username: "", 
    email: "", 
    password: "", 
    phoneNumber: "", 
    dateOfBirth: "",
    firstName: "", 
    lastName: "", 
    gender: "male", 
    show: false
  });
  
  const [states, setStates] = useState({
    error: "", 
    isLoading: false, 
    showVerification: false, 
    isPoliciesChecked: false,
    socialLoading: false, 
    showSocialModal: false, 
    showPolicyDialog: false,
    policyLoading: false, 
    socialType: null
  });
  
  const [pendingFirebaseUser, setPendingFirebaseUser] = useState(null);
  const [policyContent, setPolicyContent] = useState(null);
  const [policyType, setPolicyType] = useState("");

  useEffect(() => {
    return () => { 
      isMountedRef.current = false; 
    };
  }, []);


  const updateForm = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);
  
  const updateState = useCallback((updates) => {
    setStates(prev => ({ ...prev, ...updates }));
  }, []);

  const validateForm = useCallback(() => {
    const { username, email, password, dateOfBirth } = formData;
    const { isPoliciesChecked } = states;
    
    if (!username || !email || !password || !dateOfBirth) return "All required fields must be filled.";
    if (password.length < 10) return "Password must be at least 10 characters long.";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one capital letter.";
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return "Password must contain at least one special character.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address.";
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    if (age < 14) return "You must be at least 14 years old to sign up.";
    if (!isPoliciesChecked) return "You must agree to the terms of service and privacy policy.";
    
    return null;
  }, [formData, states.isPoliciesChecked]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    updateState({ error: "" });
    
    const validationError = validateForm();
    if (validationError) {
      if (isMountedRef.current) updateState({ error: validationError });
      return;
    }

    if (isMountedRef.current) updateState({ isLoading: true });
    
    try {
      const { username, email, password, phoneNumber, dateOfBirth } = formData;
      const additionalData = {
        firstName: "", 
        lastName: "", 
        gender: "male",
        latitude: 0, 
        longitude: 0, 
        countryCodeName: "US"
      };

      const result = await signup(username, email, password, phoneNumber ? '+' + phoneNumber : null, dateOfBirth, additionalData);
      if (result.needsVerification) updateState({ showVerification: true });
    } catch (err) {
      if (isMountedRef.current) updateState({ error: err.message || "Signup failed. Please try again." });
    } finally {
      if (isMountedRef.current) updateState({ isLoading: false });
    }
  }, [formData, signup, validateForm, updateState]);

  const handleSocialSignUp = useCallback(async (provider, type) => {
    updateState({ socialLoading: true, error: "" });
    try {
      const result = await signInWithPopup(auth, provider);
      setPendingFirebaseUser(result.user);
      updateState({ socialType: type, showSocialModal: true });
    } catch (err) {
      updateState({ error: err.message || `${type} sign up failed` });
    } finally {
      updateState({ socialLoading: false });
    }
  }, [updateState]);

  const handleGoogleSignUp = useCallback(() => {
    handleSocialSignUp(new GoogleAuthProvider(), 'google');
  }, [handleSocialSignUp]);
  
  const handleAppleSignUp = useCallback(() => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    handleSocialSignUp(provider, 'apple');
  }, [handleSocialSignUp]);

  const handleSocialSignupComplete = useCallback(async (username) => {
    try {
      updateState({ socialLoading: true });
      const result = await socialSignup(pendingFirebaseUser, states.socialType === 'google', username);
      if (result.success) {
        updateState({ showSocialModal: false });
        toast({
          title: "Successfully signed up!",
          description: `Your ${states.socialType === 'google' ? 'Google' : 'Apple'} account has been connected.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (err) {
      throw err;
    } finally {
      updateState({ socialLoading: false });
    }
  }, [pendingFirebaseUser, states.socialType, socialSignup, updateState, toast]);

  const handlePolicyClick = useCallback(async (type) => {
    setPolicyType(type);
    updateState({ policyLoading: true, showPolicyDialog: true });
    
    try {
      const response = await getPrivacyPolicyOrTerms(type);
      const content = response?.response || response || {};
      setPolicyContent({
        title: content.title || (type === "privacy policy" ? "Privacy Policy" : "Terms & Conditions"),
        description: content.description || "No content available.",
        modifiedMonth: content.modifiedMonth,
        modifiedDay: content.modifiedDay,
        modifiedYear: content.modifiedYear
      });
    } catch (err) {
      setPolicyContent({ title: "Error", description: "Failed to load content. Please try again later." });
    } finally {
      updateState({ policyLoading: false });
    }
  }, [updateState]);

  const formatDate = useCallback(() => {
    if (!policyContent?.modifiedMonth || !policyContent?.modifiedDay || !policyContent?.modifiedYear) return "";
    return `Last Updated: ${policyContent.modifiedMonth} ${policyContent.modifiedDay}, ${policyContent.modifiedYear}`;
  }, [policyContent]);


  const maxDate = useMemo(() => {
    return new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0];
  }, []);


  const handleInputChange = useCallback((field) => (e) => {
    updateForm(field, e.target.value);
  }, [updateForm]);

  const handlePhoneChange = useCallback((phone) => {
    updateForm('phoneNumber', phone);
  }, [updateForm]);

  const handleCheckboxChange = useCallback((e) => {
    updateState({ isPoliciesChecked: e.target.checked });
  }, [updateState]);

  const togglePasswordVisibility = useCallback(() => {
    updateForm('show', !formData.show);
  }, [formData.show, updateForm]);

  const handleVerificationSuccess = useCallback(() => {
    toast({
      title: "Successfully signed up!",
      description: "Your account has been created.",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  }, [toast]);


  if (states.showVerification) {
    return (
      <EmailVerification 
        email={formData.email || pendingFirebaseUser?.email}
        onBack={() => updateState({ showVerification: false, error: "" })}
        onVerified={handleVerificationSuccess}
      />
    );
  }

  return (
    <>
      <style>{`.phone-input-button .flag-dropdown, .phone-input-button .selected-flag { border-radius: 10px 0 0 10px !important; }`}</style>
      
      <Box as="form" onSubmit={handleSubmit} display="flex" flexDirection="column" alignItems="center" px="25px">
        <Text color="#fff" fontSize="26px" mb="6px">Sign Up</Text>
        <Text color="#ADA8C3" fontSize="12px" mb="28px">Let's get started!</Text>

        <HStack spacing="10px" mb="15px">
          <SocialButton 
            onClick={handleGoogleSignUp} 
            logo={GoogleLogo} 
            label="Google" 
            disabled={states.socialLoading || states.isLoading}
          />
          <SocialButton 
            onClick={handleAppleSignUp} 
            logo={AppleLogo} 
            label="Apple" 
            disabled={states.socialLoading || states.isLoading}
          />
        </HStack>

        <HStack w={FIELD_W} mb="18px">
          <Divider borderColor="#ADA8C3" />
          <Text color="#ADA8C3" fontSize="12px" px="10px">or</Text>
          <Divider borderColor="#ADA8C3" />
        </HStack>

        <InputGroup w={FIELD_W} mb="18px">
          <Input
            {...inputSt}
            type="text"
            placeholder="Username *"
            required
            value={formData.username}
            onChange={handleInputChange('username')}
            disabled={states.socialLoading}
          />
        </InputGroup>

        <InputGroup w={FIELD_W} mb="18px">
          <Input
            {...inputSt}
            type="email"
            placeholder="Email *"
            required
            value={formData.email}
            onChange={handleInputChange('email')}
            disabled={states.socialLoading}
          />
        </InputGroup>

        <Box w={FIELD_W} mb="18px">
          <PhoneInput
            country={'us'} 
            value={formData.phoneNumber}
            onChange={handlePhoneChange}
            disabled={states.socialLoading}
            inputStyle={{ 
              background: 'transparent', 
              border: '1px solid #fff', 
              borderRadius: '0 10px 10px 0',
              color: '#fff', 
              fontSize: '13px', 
              padding: '10px', 
              paddingLeft: '15px', 
              width: '100%', 
              height: '40px' 
            }}
            buttonStyle={{ 
              background: 'transparent', 
              border: '1px solid #fff', 
              borderRight: 'none',
              borderRadius: '10px 0 0 10px', 
              height: '40px', 
              width: '100px' 
            }}
            buttonClass="phone-input-button"
            dropdownStyle={{ background: '#2D3748', color: '#fff', border: '1px solid #fff' }}
            containerStyle={{ width: '100%' }}
            searchStyle={{ background: '#2D3748', color: '#fff', border: '1px solid #555' }}
            enableSearch={true} 
            searchPlaceholder="Search countries..."
          />
          <Text color="#ADA8C3" fontSize="10px" mt="2px">Phone number is optional</Text>
        </Box>

        <Box w={FIELD_W} mb="18px">
          <InputGroup mb="4px">
            <Input
              {...inputSt}
              type="date"
              placeholder="Date of Birth *"
              required
              value={formData.dateOfBirth}
              onChange={handleInputChange('dateOfBirth')}
              disabled={states.socialLoading}
              max={maxDate}
              sx={{ '&::-webkit-calendar-picker-indicator': { filter: 'invert(1)', cursor: 'pointer' } }}
            />
          </InputGroup>
          <Text color="#ADA8C3" fontSize="10px">Date of birth</Text>
        </Box>

        <InputGroup w={FIELD_W} mb="4px">
          <Input
            {...inputSt}
            type={formData.show ? "text" : "password"}
            placeholder="Password *" 
            required 
            pr="3rem"
            value={formData.password}
            onChange={handleInputChange('password')}
            disabled={states.socialLoading}
          />
          <InputRightElement width="3rem" height="100%">
            <Box 
              h="1.5rem" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              bg="transparent" 
              onClick={togglePasswordVisibility} 
              cursor="pointer"
            >
              <img src={formData.show ? EyeClose : EyeOpen} alt="toggle" width={20} />
            </Box>
          </InputRightElement>
        </InputGroup>

        <Text color="#ADA8C3" fontSize="10px" mb="12px" textAlign="center">
          Password must be at least 10 characters with a capital letter and special character
        </Text>

        <HStack w={FIELD_W} mb="12px" align="flex-start">
          <Checkbox
            isChecked={states.isPoliciesChecked}
            onChange={handleCheckboxChange}
            borderColor="#fff"
            sx={{ 
              '& .chakra-checkbox__control': { 
                borderColor: '#fff', 
                bg: 'transparent',
                _checked: { bg: '#fff', borderColor: '#fff', color: '#000' } 
              } 
            }}
          />
          <Text fontSize="10px" color="#ADA8C3">
            I agree to the{" "}
            <Link 
              color="#fff" 
              textDecoration="underline" 
              onClick={(e) => {
                e.preventDefault(); 
                handlePolicyClick("terms conditions");
              }} 
              cursor="pointer"
            >
              terms of service
            </Link>
            {" "}and{" "}
            <Link 
              color="#fff" 
              textDecoration="underline" 
              onClick={(e) => {
                e.preventDefault(); 
                handlePolicyClick("privacy policy");
              }} 
              cursor="pointer"
            >
              privacy policy
            </Link>
          </Text>
        </HStack>

        {states.error && (
          <Text color="red" fontSize="12px" mt="8px" textAlign="center" fontWeight="bold">
            {states.error}
          </Text>
        )}

        {states.isLoading || states.socialLoading ? (
          <Spinner color="white" mt="15px" w="30px" h="30px" thickness="4px" speed="0.65s" />
        ) : (
          <Button 
            type="submit" 
            w={FIELD_W} 
            mt="15px" 
            p="10px" 
            bg="#fff" 
            color="#000"
            borderRadius="10px" 
            _hover={{ cursor: "pointer", bg: "transparent", color: "#fff", outline: "1px solid #fff" }}
            disabled={!states.isPoliciesChecked}
          >
            Sign Up
          </Button>
        )}

        <Text fontSize="12px" color="#fff" mt="15px" textAlign="center">
          Already have an account?{" "}
          <Link 
            href="#" 
            fontWeight="bold" 
            color="#fff" 
            onClick={e => { 
              e.preventDefault(); 
              onBack(); 
            }}
          >
            Login
          </Link>
        </Text>
      </Box>

      {states.showSocialModal && (
        <SocialSignupModal
          firebaseUser={pendingFirebaseUser}
          socialType={states.socialType}
          onComplete={handleSocialSignupComplete}
          onCancel={() => { 
            updateState({ showSocialModal: false }); 
            setPendingFirebaseUser(null); 
          }}
        />
      )}

      <Modal 
        isOpen={states.showPolicyDialog} 
        onClose={() => updateState({ showPolicyDialog: false })}
        size="xl" 
        isCentered
      >
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent 
          bg="rgba(255,255,255,0.95)" 
          border="1px solid #fff" 
          borderRadius="10px"
          maxH="80vh" 
          color="#000"
        >
          <ModalHeader>
            {states.policyLoading ? <Spinner size="sm" /> : (
              <Box>
                <Text fontSize="18px" fontWeight="bold">{policyContent?.title || policyType}</Text>
                {policyContent && <Text fontSize="12px" color="gray.600" mt="4px">{formatDate()}</Text>}
              </Box>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb="20px">
            {states.policyLoading ? (
              <Box textAlign="center" py="20px">
                <Spinner />
                <Text mt="10px" color="gray.600">Loading...</Text>
              </Box>
            ) : (
              <Box 
                dangerouslySetInnerHTML={{ __html: policyContent?.description || "No content available." }}
                sx={{ 
                  '& p': { marginBottom: '12px' }, 
                  '& h1, & h2, & h3': { marginTop: '16px', marginBottom: '8px', fontWeight: 'bold' },
                  '& ul, & ol': { paddingLeft: '20px', marginBottom: '12px' }, 
                  '& li': { marginBottom: '4px' },
                  '& a': { color: 'blue.500', textDecoration: 'underline' } 
                }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}